import { DAY, MINUTE, time } from './time';

export const ROOT = 'https://culios.app';

const WARMUP_TIME = 5 * MINUTE;

export const isValidHttpsUrl = (s?: string | null, includeBlobs = true) => {
    if (!s) return false;

    let url;

    try {
        url = new URL(s);
    } catch (_) {
        return false;
    }

    return url.protocol === 'https:' || (includeBlobs && isBlob(s));
};

/**
 * @deprecated use isValidHttpsUrl
 * @param s
 * @returns
 */
export const isValidHttpUrl = (s?: string | null) => {
    if (!s) return false;

    let url;

    try {
        url = new URL(s);
    } catch (_) {
        return false;
    }

    return url.protocol === 'http:' || url.protocol === 'https:' || url.toString().startsWith('blob:');
};

export const isValidUrl = (s?: string | null) => {
    try {
        return !!new URL(s ?? '');
    } catch (_) {
        return false;
    }
};

export const generateNameFromFile = (name: string, forceExt?: string) => {
    const parts = name?.split('.') ?? [];
    const ext = parts.findLast((_, idx) => idx > 0) ?? 'jpg';
    if (parts.length > 1) parts.pop();
    parts.push(new Date().getTime().toString());
    return `${parts.map((it) => it.replace(/[^0-9a-z]/gi, '')).join('_')}.${forceExt ?? ext}`;
};

export const rootPath = () => {
    const a = window.location.href.split('/');
    return `${a[0]}//${a[2]}`;
};

export const isBlob = (url: string | null | undefined) => !!url && url.startsWith('blob:');

export const toFullUrl = (path?: string | null) => {
    if (path?.startsWith('/')) path = path.substring(1);
    if (!path?.trim()) return ROOT;
    return `${ROOT}/${path.trim()}`;
};

export const toAppPath = (path?: string | null) => {
    if (path?.startsWith('/')) return path;
    if (!path) return '/';
    if (path.toLowerCase().startsWith(ROOT)) path = path.substring(ROOT.length);
    if (isValidHttpsUrl(path)) return path;
    if (!path.startsWith('/')) path = '/' + path;
    return path;
};

export const getNextClipUrl = (url?: string | null, loop: boolean = false): string | null => {
    if (!url) {
        return null;
    }

    const lastSlashIndex = url.lastIndexOf('/');
    const path = lastSlashIndex === -1 ? '' : url.substring(0, lastSlashIndex + 1);
    const filenameWithExt = lastSlashIndex === -1 ? url : url.substring(lastSlashIndex + 1);

    if (!filenameWithExt || !filenameWithExt.endsWith('.mp4')) {
        return null;
    }

    const filename = filenameWithExt.substring(0, filenameWithExt.length - 4); // remove .mp4

    const parts = filename.split('_');
    if (parts.length < 4) {
        return null;
    }

    const segmentPart = parts.pop(); // e.g., '1of4'
    const timestampPart = parts.pop(); // e.g., '123456'
    const extPart = parts.pop(); // e.g., 'webm'
    const originalFilename = parts.join('_'); // e.g., 'video' or 'video_with_underscores'

    if (!segmentPart || !timestampPart || !extPart || !originalFilename) {
        return null;
    }

    // Validate timestamp
    const clipTime = parseInt(timestampPart, 10) * MINUTE; // the minute at which clip was created since 1970
    const now = time();
    if (isNaN(clipTime) || clipTime > now || clipTime < now - DAY * 365 * 50) {
        // clip time should be in reasonable timeframe
        return null;
    }

    if (now - clipTime < WARMUP_TIME) {
        // video might not be ready. return original file source for now.
        return `${path}${originalFilename}.${extPart}?d=transcoding`;
    }

    // Validate and parse segment
    const segmentParts = segmentPart.split('of');
    if (segmentParts.length !== 2) {
        return null;
    }

    const current = parseInt(segmentParts[0], 10);
    const totalStr = segmentParts[1];
    const total = totalStr === 'N' ? Number.MAX_SAFE_INTEGER : parseInt(totalStr);

    if (isNaN(current) || isNaN(total) || current <= 0) {
        return null;
    }

    if (current > total) {
        return null;
    }

    let next = current + 1;

    if (next > total) {
        if (loop) {
            next = 1;
        } else {
            return null;
        }
    }

    const newFilename = `${originalFilename}_${extPart}_${timestampPart}_${next}of${totalStr}.mp4`;
    return `${path}${newFilename}`;
};
